// app/oauth2/redirect/page.tsx
"use client";
import { useEffect } from "react";
import { useRouter } from "next/navigation";
import {
  useAppDispatch,
  useSaveStateToLocalStorageOnChange,
  useSetInitialStore,
} from "lib/redux/hooks";
import { setAccessToken } from "lib/redux/uiSlice";

const DEFAULT_REDIRECT = "/dashboard";

const getCurrentPage = (currentPage: string | null) => {
  if (!currentPage) return DEFAULT_REDIRECT;

  const validPages = [
    "/",
    "/resume-builder",
    "/premium-subscription",
    "/resume-writing",
    "/upload-resume",
    "/select-experience",
    "/score-check",
    "/score-check/overall",
    "/score-check/personal",
    "/score-check/experience",
    "/score-check/education",
    "/score-check/skills",
    "/score-check/projects",
  ];

  if (validPages.includes(currentPage)) {
    return currentPage === "/" ? DEFAULT_REDIRECT : currentPage;
  }

  return DEFAULT_REDIRECT;
};

const OAuth2RedirectHandler = () => {
  useSetInitialStore();
  useSaveStateToLocalStorageOnChange();
  const router = useRouter();
  const dispatch = useAppDispatch();

  useEffect(() => {
    const getItemWithRetry = async (key: string, maxRetries = 5, retryDelay = 200): Promise<string | null> => {
      let retries = 0;
      let value = null;
      
      while (retries < maxRetries) {
        try {
          value = localStorage.getItem(key);
          if (value !== null) {
            return value;
          }
        } catch (e) {
          console.error(`Error accessing localStorage (attempt ${retries + 1}):`, e);
        }
        
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        retries++;
      }
      
      return null;
    };

    const handleRedirect = async () => {
      try {
        // Extract token from URL
        const { searchParams } = new URL(window.location.href);
        const accessToken = searchParams.get("token");

        if (!accessToken) {
          console.error("No access token found in URL");
          router.push("/");
          return;
        }

        // Save the access token in local storage with retry
        try {
          localStorage.setItem("access-token", accessToken);
          dispatch(setAccessToken(accessToken));
        } catch (e) {
          console.error("Failed to save access token to localStorage:", e);
        }

        const currentPage = await getItemWithRetry("current-page");
        
        const redirectPage = getCurrentPage(currentPage);
        
        router.push(redirectPage);
      } catch (error) {
        console.error("Error during redirect handling:", error);
        router.push(DEFAULT_REDIRECT); // Fallback to default
      }
    };

    handleRedirect();
  }, [router, dispatch]);

  return (
    <div className="flex h-screen flex-col items-center justify-center bg-gray-100">
      <div className="flex flex-col items-center">
        <h1 className="mb-4 text-2xl font-bold text-gray-800">
          Redirecting...
        </h1>
        <p className="mb-8 text-gray-600">Please wait while we log you in...</p>
        <div className="loader"></div>
      </div>
      <style jsx>{`
        .loader {
          border: 8px solid rgba(0, 0, 0, 0.1);
          border-left-color: #3b82f6;
          border-radius: 50%;
          width: 50px;
          height: 50px;
          animation: spin 1s infinite linear;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

export default OAuth2RedirectHandler;
